'use client';

import { motion } from 'framer-motion';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { components } from '@/types/strapi';

export default function EventVideo({
	heading,
	videos,
	isFirst = false,
}: components['schemas']['DynamicZoneEventVideoComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<section className="event-video-section bg-gradient-to-b from-gray-50 via-gray-50 to-white px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-24">
			<noscript>
				<style>{`
					.event-video-section h2[style],
					.event-video-section h3[style],
					.event-video-section p[style],
					.event-video-section div[style*='opacity'],
					.event-video-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, margin: '-100px' }}
					transition={{
						duration: 0.5,
						ease: [0.25, 0.1, 0.25, 1],
					}}
					className="mb-12 text-center">
					<SectionHeading className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
						{heading}
					</SectionHeading>
				</motion.div>

				<div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
					{videos?.map((video, index) => (
						<motion.div
							key={video.video_url}
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: '-100px' }}
							transition={{
								delay: index * 0.1,
								duration: 0.5,
								ease: [0.25, 0.1, 0.25, 1],
							}}
							className="flex flex-col space-y-4">
							<div className="relative aspect-video w-full overflow-hidden rounded-xl bg-gray-100 shadow-[0_10px_25px_-12px_rgba(0,0,0,0.15)] ring-1 ring-gray-200/70">
								<div className="absolute inset-0 bg-gradient-to-br from-gray-900/5 to-transparent opacity-50" />
								<iframe
									src={video.video_url}
									title={video.title ?? 'Default Title'}
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
									className="absolute inset-0 h-full w-full"
								/>
							</div>
							<div className="space-y-2">
								{video.title && (
									<h3 className="text-xl font-semibold text-gray-900">
										{video.title}
									</h3>
								)}

								{video.description && (
									<p className="text-base text-gray-600">
										{video.description}
									</p>
								)}
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}
